import React from 'react';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

interface BannerContainerProps {
  variant: 'gradient' | 'alert';
  gradientFrom?: string;
  gradientTo?: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
  theme?: 'trial' | 'trial_expired' | 'warning' | 'default';
}

export const BannerContainer: React.FC<BannerContainerProps> = ({
  variant,
  gradientFrom,
  gradientTo,
  children,
  onDismiss,
  className = '',
  theme = 'default'
}) => {
  const getContainerClasses = () => {
    if (variant === 'gradient') {
      // Custom gradient or theme-based gradient
      if (gradientFrom && gradientTo) {
        return `bg-gradient-to-br ${gradientFrom} ${gradientTo} text-white border-0 shadow-2xl`;
      }
      
      // Theme-based gradients
      switch (theme) {
        case 'trial':
          return 'bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white border-0 shadow-2xl';
        case 'trial_expired':
          return 'bg-gradient-to-br from-red-600 via-orange-600 to-red-700 text-white border-0 shadow-2xl';
        case 'warning':
          return 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200';
        default:
          return 'bg-muted/50 border-2';
      }
    }
    
    // Alert variant
    return 'border-2 shadow-2xl backdrop-blur-sm bg-white/95 dark:bg-gray-900/95';
  };

  const getCloseButtonClasses = () => {
    if (variant === 'gradient') {
      switch (theme) {
        case 'warning':
          return 'text-orange-700/70 hover:text-orange-700 hover:bg-orange-100/20';
        default:
          return 'text-white/70 hover:text-white hover:bg-white/20';
      }
    }
    return 'opacity-60 hover:opacity-100';
  };

  const getDecorativeElements = () => {
    if (variant !== 'gradient') return null;

    const circleColors = theme === 'warning' 
      ? {
          large: 'bg-orange-400/10',
          small: 'bg-yellow-400/5'
        }
      : {
          large: 'bg-white/10',
          small: 'bg-white/5'
        };

    return (
      <>
        <div className={`absolute top-0 right-0 w-32 h-32 ${circleColors.large} rounded-full -translate-y-8 translate-x-8`}></div>
        <div className={`absolute bottom-0 left-0 w-24 h-24 ${circleColors.small} rounded-full translate-y-8 -translate-x-8`}></div>
      </>
    );
  };

  return (
    <div className={`${getContainerClasses()} rounded-2xl p-8 relative overflow-hidden ${className}`}>
      {/* Decorative background elements */}
      {getDecorativeElements()}
      
      {/* Close button */}
      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className={`absolute top-4 right-4 h-8 w-8 p-0 ${getCloseButtonClasses()} rounded-full`}
        >
          <X className="h-5 w-5" />
        </Button>
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};